//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { useQuery } from "@tanstack/react-query";
import apiClients from "../../../api/apiclients";
import { CreditUsage, GetCreditUsageRequest, Granularity } from "../../../api/usage/v1/usage";
import { useDashboardContext } from "../../DashboardContextProvider";
import { UsageRangeKey, usageRangeFor } from "./usageRange";

export const useFetchCreditUsage = ({
  range,
  now,
  deploymentId,
  component,
  groupByDeployment = false,
  groupByComponent = false,
  granularity: granularityOverride,
  period,
}: {
  range: UsageRangeKey;
  // "Now", required (no internal `new Date()` fallback) so the caller
  // computes it exactly once per render and passes the SAME value to every
  // useFetchCreditUsage call (chart and breakdown) as well as to any
  // usageRangeFor(range, now) call it does itself for clamping - see
  // OrganizationUsageView/DeploymentUsageView. Each call independently doing
  // `new Date()` used to mean a render straddling the UTC midnight boundary
  // between two of those calls could derive DIFFERENT [from, to) windows for
  // the same `range` within one render, so chart/breakdown queries and the
  // clamp could disagree about what "the selected range" even was.
  now: Date;
  deploymentId?: string;
  // Optional single usage component to filter to, e.g. "MemoryHour" - see
  // UsageComponentFilter. Follows the exact same omit-when-unset pattern as
  // deploymentId below.
  component?: string;
  groupByDeployment?: boolean;
  groupByComponent?: boolean;
  // Optional override for the requested bucket granularity. When omitted,
  // granularity is derived from `range` via usageRangeFor (DAY or MONTH) as
  // before. A caller that only wants a single aggregate bucket - e.g. a
  // component breakdown, which does not need a time series at all - passes
  // Granularity.GRANULARITY_TOTAL here so the server returns one bucket per
  // group instead of one bucket per group per period.
  granularity?: Granularity;
  // Optional override for the [from, to) window, bypassing the one derived
  // from `range`. Used to scope a request down to a single bucket the viewer
  // clicked on in the chart (see periodBoundsFor) - `range` is still required
  // in that case, since it still determines the underlying granularity, and
  // the caller typically still shows the full-range chart alongside the
  // narrowed breakdown.
  period?: { from: Date; to: Date };
}) => {
  const { selectedOrganization } = useDashboardContext();
  const organizationId = selectedOrganization?.id || "";
  const { from: rangeFrom, to: rangeTo, granularity: rangeGranularity } = usageRangeFor(range, now);
  const from = period?.from ?? rangeFrom;
  const to = period?.to ?? rangeTo;
  // The granularity actually sent on the wire, and the value returned below.
  // Using `??` (not `||`) matters here: GRANULARITY_TOTAL is enum value 0,
  // which `||` would treat as falsy and incorrectly fall back to rangeGranularity.
  const granularity = granularityOverride ?? rangeGranularity;

  const query = useQuery<CreditUsage>({
    // from/to (not `range`) are what actually vary when `period` narrows the
    // request down to a single clicked bucket - range alone would collide
    // two different clicked days sharing the same overall range into one
    // cache entry.
    queryKey: [
      "creditUsage",
      organizationId,
      deploymentId || "",
      component || "",
      from.getTime(),
      to.getTime(),
      groupByDeployment,
      groupByComponent,
      granularity,
    ],
    queryFn: () => {
      // deployment_id and component must be OMITTED from the request object
      // (not set to undefined) when unselected. api.queryString() builds the
      // GET query string with `for (const key in obj)`, which iterates every
      // key PRESENT on the object regardless of its value. A key that is
      // present-but-undefined still serialises, via
      // encodeURIComponent(undefined), to the literal string "undefined" -
      // for component, the server would then reject that as an unknown
      // usage component instead of treating it as unset.
      const req: GetCreditUsageRequest = {
        organization_id: organizationId,
        from,
        to,
        granularity,
        group_by_deployment: groupByDeployment,
        group_by_component: groupByComponent,
      };
      if (deploymentId) {
        req.deployment_id = deploymentId;
      }
      if (component) {
        req.component = component;
      }
      return apiClients.usageClient.GetCreditUsage(req);
    },
    enabled: !!organizationId,
  });

  return { data: query.data, isLoading: query.isLoading, error: query.error, granularity };
};
